import { useMemo } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import {
  FileText,
  Globe,
  StickyNote,
  Quote,
  Upload,
  BookOpen,
  ChevronRight,
} from 'lucide-react';
import { CitationWithSource } from '@/hooks/useProjectCitations';
import { Chapter, Section } from '@/hooks/useChapters';
import { useUIStore } from '@/stores/uiStore';
import { cn } from '@/lib/utils';

const typeIcons: Record<string, React.ElementType> = {
  note: StickyNote,
  upload: Upload,
  web_snapshot: Globe,
  quote: Quote,
};

interface CitationsByChapterProps {
  citations: CitationWithSource[];
  chapters: Chapter[];
  sections: Record<string, Section[]>;
  projectId: string;
}

interface GroupedCitations {
  chapter: Chapter;
  sections: {
    section: Section;
    citations: CitationWithSource[];
  }[];
  unassignedInChapter: CitationWithSource[];
}

export function CitationsByChapter({
  citations,
  chapters,
  sections,
  projectId,
}: CitationsByChapterProps) {
  const { closeCitationsManager } = useUIStore();
  
  // Group citations by chapter and section
  const groupedData = useMemo(() => {
    const result: GroupedCitations[] = [];
    const unassigned: CitationWithSource[] = [];
    
    // First, handle unassigned citations
    citations.forEach(citation => {
      if (!citation.section_id) {
        unassigned.push(citation);
      }
    });
    
    // Group by chapter
    chapters.forEach(chapter => {
      const chapterSections = sections[chapter.id] || [];
      const sectionGroups: GroupedCitations['sections'] = [];
      
      chapterSections.forEach(section => {
        const sectionCitations = citations.filter(c => c.section_id === section.id);
        if (sectionCitations.length > 0) {
          sectionGroups.push({
            section,
            citations: sectionCitations,
          });
        }
      });
      
      if (sectionGroups.length > 0) {
        result.push({
          chapter,
          sections: sectionGroups,
          unassignedInChapter: [],
        });
      }
    });
    
    return { grouped: result, unassigned };
  }, [citations, chapters, sections]);

  const { grouped, unassigned } = groupedData;
  const totalGrouped = grouped.reduce((sum, g) => 
    sum + g.sections.reduce((s, sec) => s + sec.citations.length, 0), 0
  );

  if (citations.length === 0) {
    return (
      <div className="py-8 text-center">
        <BookOpen className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
        <p className="text-muted-foreground">No citations yet</p>
        <p className="text-sm text-muted-foreground/70">
          Citations will be organized by chapter here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Unassigned Citations */}
      {unassigned.length > 0 && (
        <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-amber-600">Unassigned</span>
            <Badge variant="outline" className="text-amber-600 border-amber-500/30">
              {unassigned.length}
            </Badge>
          </div>
          <div className="space-y-1.5">
            {unassigned.map(citation => {
              const Icon = typeIcons[citation.research_item.type] || FileText;
              return (
                <div key={citation.id} className="flex items-center gap-2 text-sm">
                  <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="truncate">
                    {citation.research_item.source_author || citation.research_item.title}
                  </span>
                  {citation.is_direct_quote && (
                    <Badge variant="secondary" className="text-[10px] px-1">Q</Badge>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Chapters Accordion */}
      {grouped.length > 0 && (
        <Accordion type="multiple" className="space-y-2">
          {grouped.map(({ chapter, sections: sectionGroups }) => {
            const chapterCitationCount = sectionGroups.reduce(
              (sum, s) => sum + s.citations.length, 0
            );
            
            return (
              <AccordionItem 
                key={chapter.id} 
                value={chapter.id}
                className="border rounded-lg px-3"
              >
                <AccordionTrigger className="hover:no-underline py-3">
                  <div className="flex items-center gap-2 flex-1">
                    <BookOpen className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">{chapter.title}</span>
                    <Badge variant="secondary" className="ml-auto mr-2">
                      {chapterCitationCount}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-3">
                  <div className="space-y-3 pt-1">
                    {sectionGroups.map(({ section, citations: sectionCitations }) => (
                      <div key={section.id} className="ml-4">
                        <div className="flex items-center gap-2 mb-2">
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm font-medium text-muted-foreground">
                            {section.title}
                          </span>
                          <span className="text-xs text-muted-foreground/70">
                            ({sectionCitations.length})
                          </span>
                        </div>
                        <div className="ml-5 space-y-1.5">
                          {sectionCitations.map(citation => {
                            const Icon = typeIcons[citation.research_item.type] || FileText;
                            return (
                              <div 
                                key={citation.id} 
                                className="flex items-center gap-2 text-sm p-1.5 rounded hover:bg-muted/50 transition-colors"
                              >
                                <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                <span className="truncate flex-1">
                                  {citation.research_item.source_author || citation.research_item.title}
                                </span>
                                {citation.page_reference && (
                                  <span className="text-xs text-muted-foreground shrink-0">
                                    p. {citation.page_reference}
                                  </span>
                                )}
                                {citation.is_direct_quote && (
                                  <Badge variant="secondary" className="text-[10px] px-1 shrink-0">
                                    Quote
                                  </Badge>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
      
      {grouped.length === 0 && unassigned.length > 0 && (
        <div className="text-center py-4 text-sm text-muted-foreground">
          All citations are unassigned to sections
        </div>
      )}
    </div>
  );
}
